import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  List,
  ListItem,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  PlayArrow as TestIcon,
  Bolt as RunIcon,
  Science as ScienceIcon,
  AutoFixHigh as RuleIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { usePermission } from '../../hooks/usePermission';
import { formatRelativeTime } from '../../utils/dateUtils';

const ROLE_LABELS = {
  company_admin: 'Company Admin',
  admin: 'Admin',
  manager: 'Manager',
  senior_analyst: 'Senior Analyst',
  analyst: 'Analyst',
};

const OPERATOR_WORDS = {
  equals: 'is',
  not_equals: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  regex: 'matches',
  in: 'is one of',
  not_in: 'is not one of',
  gte: '≥',
  lte: '≤',
  exists: 'is set',
  not_exists: 'is empty',
};

const NO_VALUE_OPERATORS = ['exists', 'not_exists'];
const FREE_TEXT_LIST_OPERATORS = ['in', 'not_in'];

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const localId = () => `new-${Math.random().toString(36).slice(2, 10)}`;

const emptyCondition = () => ({ id: localId(), field: '', operator: '', value: '' });
const emptyAction = () => ({ id: localId(), type: '', params: {} });
const emptyRule = () => ({
  name: '',
  description: '',
  priority: 100,
  enabled: true,
  stopProcessing: false,
  conditions: { match: 'all', items: [emptyCondition()] },
  actions: [emptyAction()],
});

/** Rule -> form shape (tags param becomes a comma string for editing). */
const ruleToForm = (rule) => ({
  name: rule.name || '',
  description: rule.description || '',
  priority: rule.priority || 100,
  enabled: rule.enabled !== false,
  stopProcessing: !!rule.stopProcessing,
  conditions: {
    match: rule.conditions?.match === 'any' ? 'any' : 'all',
    items: (rule.conditions?.items || []).map((c) => ({ id: localId(), field: c.field || '', operator: c.operator || '', value: c.value === undefined || c.value === null ? '' : c.value })),
  },
  actions: (rule.actions || []).map((a) => ({
    id: localId(),
    type: a.type || '',
    params: a.type === 'add_tags'
      ? { ...a.params, tags: Array.isArray(a.params?.tags) ? a.params.tags.join(', ') : (a.params?.tags || '') }
      : { ...a.params },
  })),
});

/** Form -> API payload (tags string becomes an array; ids are stripped). */
const formToPayload = (form) => ({
  name: form.name.trim(),
  description: form.description.trim(),
  priority: Math.min(1000, Math.max(1, parseInt(form.priority, 10) || 100)),
  enabled: form.enabled,
  stopProcessing: form.stopProcessing,
  conditions: {
    match: form.conditions.match,
    items: form.conditions.items
      .filter((c) => c.field && c.operator)
      .map((c) => ({ field: c.field, operator: c.operator, value: NO_VALUE_OPERATORS.includes(c.operator) ? '' : c.value })),
  },
  actions: form.actions
    .filter((a) => a.type)
    .map((a) => ({
      type: a.type,
      params: a.type === 'add_tags'
        ? { tags: String(a.params.tags || '').split(',').map((t) => t.trim()).filter(Boolean) }
        : { ...a.params },
    })),
});

const humanizeCondition = (cond, fieldsById) => {
  const field = fieldsById[cond.field];
  const label = field?.label || cond.field || '(field)';
  const word = OPERATOR_WORDS[cond.operator] || cond.operator || '';
  if (!cond.operator) return `${label} – incomplete`;
  if (NO_VALUE_OPERATORS.includes(cond.operator)) return `${label} ${word}`;
  const value = Array.isArray(cond.value) ? cond.value.join(', ') : cond.value;
  return `${label} ${word} ${value ?? ''}`.trim();
};

const humanizeAction = (action, options) => {
  const meta = (options.actions || []).find((a) => a.id === action.type);
  const label = meta?.label || action.type;
  const p = action.params || {};
  switch (action.type) {
    case 'set_severity': return `${label}: ${p.severity || '?'}`;
    case 'set_status': return `${label}: ${p.status || '?'}`;
    case 'add_tags': {
      const tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',').map((t) => t.trim()).filter(Boolean);
      return `${label}: ${tags.join(', ') || '?'}`;
    }
    case 'assign': {
      const user = (options.users || []).find((u) => u.id === p.userId);
      return `${label}: ${user ? user.name : p.userId || '?'}`;
    }
    case 'create_case': {
      const pb = (options.playbooks || []).find((x) => x.id === p.playbookId);
      return `${label}${pb ? ` (${pb.name})` : ''}`;
    }
    default: return label;
  }
};

/* ------------------------------------------------------------------ */
/* Condition + action row editors                                      */
/* ------------------------------------------------------------------ */
const ConditionRow = ({ cond, fields, operators, onChange, onRemove, disabled }) => {
  const fieldMeta = fields.find((f) => f.id === cond.field);
  const availableOperators = fieldMeta ? operators.filter((o) => o.types.includes(fieldMeta.type)) : operators;
  const showValue = cond.operator && !NO_VALUE_OPERATORS.includes(cond.operator);
  const useSelectValue = fieldMeta?.values && showValue && !FREE_TEXT_LIST_OPERATORS.includes(cond.operator);

  return (
    <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap" sx={{ mb: 1.5 }}>
      <FormControl size="small" sx={{ minWidth: 180 }} disabled={disabled}>
        <InputLabel>Field</InputLabel>
        <Select
          label="Field"
          value={cond.field}
          onChange={(e) => onChange({ ...cond, field: e.target.value, operator: '', value: '' })}
        >
          {fields.map((f) => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 180 }} disabled={disabled || !cond.field}>
        <InputLabel>Operator</InputLabel>
        <Select
          label="Operator"
          value={cond.operator}
          onChange={(e) => onChange({ ...cond, operator: e.target.value, value: '' })}
        >
          {availableOperators.map((o) => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
        </Select>
      </FormControl>
      {showValue && (
        useSelectValue ? (
          <FormControl size="small" sx={{ minWidth: 160 }} disabled={disabled}>
            <InputLabel>Value</InputLabel>
            <Select label="Value" value={cond.value} onChange={(e) => onChange({ ...cond, value: e.target.value })}>
              {fieldMeta.values.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </Select>
          </FormControl>
        ) : (
          <TextField
            size="small"
            label={FREE_TEXT_LIST_OPERATORS.includes(cond.operator) ? 'Value (comma separated)' : 'Value'}
            type={fieldMeta?.type === 'number' ? 'number' : 'text'}
            value={cond.value}
            onChange={(e) => onChange({ ...cond, value: e.target.value })}
            disabled={disabled}
            sx={{ minWidth: 200 }}
          />
        )
      )}
      <Tooltip title="Remove condition">
        <span>
          <IconButton size="small" color="error" onClick={onRemove} disabled={disabled} sx={{ mt: 0.5 }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

const ActionRow = ({ action, options, onChange, onRemove, disabled }) => {
  const setParams = (params) => onChange({ ...action, params });
  const severityValues = options.fields.find((f) => f.id === 'severity')?.values || ['Low', 'Medium', 'High', 'Critical'];
  const statusValues = options.fields.find((f) => f.id === 'status')?.values || ['New', 'In Progress', 'Under Review', 'Resolved', 'False Positive', 'Closed'];

  let paramFields = null;
  switch (action.type) {
    case 'set_severity':
      paramFields = (
        <FormControl size="small" sx={{ minWidth: 160 }} disabled={disabled}>
          <InputLabel>Severity</InputLabel>
          <Select label="Severity" value={action.params.severity || ''} onChange={(e) => setParams({ severity: e.target.value })}>
            {severityValues.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </Select>
        </FormControl>
      );
      break;
    case 'set_status':
      paramFields = (
        <FormControl size="small" sx={{ minWidth: 180 }} disabled={disabled}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={action.params.status || ''} onChange={(e) => setParams({ status: e.target.value })}>
            {statusValues.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </Select>
        </FormControl>
      );
      break;
    case 'add_tags':
      paramFields = (
        <TextField
          size="small"
          label="Tags (comma separated)"
          value={action.params.tags || ''}
          onChange={(e) => setParams({ ...action.params, tags: e.target.value })}
          disabled={disabled}
          sx={{ minWidth: 240 }}
        />
      );
      break;
    case 'assign':
      paramFields = (
        <FormControl size="small" sx={{ minWidth: 220 }} disabled={disabled}>
          <InputLabel>User</InputLabel>
          <Select label="User" value={action.params.userId || ''} onChange={(e) => setParams({ userId: e.target.value })}>
            {options.users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name} ({u.role})</MenuItem>)}
          </Select>
        </FormControl>
      );
      break;
    case 'create_case':
      paramFields = (
        <Box display="flex" gap={1} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
            <InputLabel>Playbook (optional)</InputLabel>
            <Select label="Playbook (optional)" value={action.params.playbookId || ''} onChange={(e) => setParams({ ...action.params, playbookId: e.target.value })}>
              <MenuItem value=""><em>None</em></MenuItem>
              {options.playbooks.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }} disabled={disabled}>
            <InputLabel>Priority (optional)</InputLabel>
            <Select label="Priority (optional)" value={action.params.priority || ''} onChange={(e) => setParams({ ...action.params, priority: e.target.value })}>
              <MenuItem value=""><em>Use severity</em></MenuItem>
              {severityValues.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Title template (optional)"
            placeholder="{{title}} on {{host}}"
            helperText="{{title}} {{host}} {{severity}} {{category}} {{source}}"
            value={action.params.titleTemplate || ''}
            onChange={(e) => setParams({ ...action.params, titleTemplate: e.target.value })}
            disabled={disabled}
            sx={{ minWidth: 260 }}
          />
        </Box>
      );
      break;
    case 'suppress':
      paramFields = (
        <TextField
          size="small"
          label="Reason (optional)"
          value={action.params.reason || ''}
          onChange={(e) => setParams({ reason: e.target.value })}
          disabled={disabled}
          sx={{ minWidth: 260 }}
        />
      );
      break;
    case 'add_comment':
      paramFields = (
        <TextField
          size="small"
          label="Comment text"
          placeholder="Auto-triaged: {{title}} ({{severity}})"
          helperText="Supports {{title}} {{host}} {{severity}} {{category}} {{source}}"
          value={action.params.text || ''}
          onChange={(e) => setParams({ text: e.target.value })}
          disabled={disabled}
          fullWidth
          sx={{ minWidth: 280 }}
        />
      );
      break;
    case 'notify':
      paramFields = (
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="flex-start">
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
            <InputLabel>Roles</InputLabel>
            <Select
              multiple
              label="Roles"
              input={<OutlinedInput label="Roles" />}
              value={action.params.roles || []}
              onChange={(e) => setParams({ ...action.params, roles: e.target.value })}
              renderValue={(selected) => (
                <Box display="flex" gap={0.5} flexWrap="wrap">
                  {selected.map((r) => <Chip key={r} label={ROLE_LABELS[r] || r} size="small" />)}
                </Box>
              )}
            >
              {(options.roles || []).map((r) => <MenuItem key={r} value={r}>{ROLE_LABELS[r] || r}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }} disabled={disabled}>
            <InputLabel>Users</InputLabel>
            <Select
              multiple
              label="Users"
              input={<OutlinedInput label="Users" />}
              value={action.params.userIds || []}
              onChange={(e) => setParams({ ...action.params, userIds: e.target.value })}
              renderValue={(selected) => (
                <Box display="flex" gap={0.5} flexWrap="wrap">
                  {selected.map((id) => <Chip key={id} label={options.users.find((u) => u.id === id)?.name || id} size="small" />)}
                </Box>
              )}
            >
              {options.users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name} ({u.role})</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Message (optional)"
            placeholder="{{title}} matched"
            value={action.params.message || ''}
            onChange={(e) => setParams({ ...action.params, message: e.target.value })}
            disabled={disabled}
            sx={{ minWidth: 220 }}
          />
          <FormControlLabel
            control={<Switch checked={!!action.params.webhooks} onChange={(e) => setParams({ ...action.params, webhooks: e.target.checked })} disabled={disabled} />}
            label="Also send webhooks"
          />
        </Box>
      );
      break;
    default:
      paramFields = null;
  }

  return (
    <Box sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box display="flex" gap={1} alignItems="flex-start" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
          <InputLabel>Action</InputLabel>
          <Select label="Action" value={action.type} onChange={(e) => onChange({ ...action, type: e.target.value, params: {} })}>
            {options.actions.map((a) => <MenuItem key={a.id} value={a.id}>{a.label}</MenuItem>)}
          </Select>
        </FormControl>
        {paramFields}
        <Tooltip title="Remove action">
          <span>
            <IconButton size="small" color="error" onClick={onRemove} disabled={disabled} sx={{ mt: 0.5 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

/** Required-param check mirroring the backend's validateRule, for the Save button state. */
const actionIsComplete = (action) => {
  const p = action.params || {};
  switch (action.type) {
    case 'set_severity': return !!p.severity;
    case 'set_status': return !!p.status;
    case 'add_tags': return !!String(p.tags || '').trim();
    case 'assign': return !!p.userId;
    case 'add_comment': return !!String(p.text || '').trim();
    case 'create_case': case 'suppress': case 'notify': return true;
    default: return false;
  }
};

/* ------------------------------------------------------------------ */
/* Rule editor dialog                                                   */
/* ------------------------------------------------------------------ */
const RuleDialog = ({ open, rule, options, onClose, onSave, saving, serverErrors }) => {
  const [form, setForm] = useState(emptyRule());
  const [testState, setTestState] = useState({ loading: false, error: null, result: null });

  useEffect(() => {
    if (open) {
      setForm(rule ? ruleToForm(rule) : emptyRule());
      setTestState({ loading: false, error: null, result: null });
    }
  }, [open, rule]);

  const setCondition = (idx, next) => setForm((f) => ({ ...f, conditions: { ...f.conditions, items: f.conditions.items.map((c, i) => (i === idx ? next : c)) } }));
  const removeCondition = (idx) => setForm((f) => ({ ...f, conditions: { ...f.conditions, items: f.conditions.items.filter((_, i) => i !== idx) } }));
  const addCondition = () => setForm((f) => ({ ...f, conditions: { ...f.conditions, items: [...f.conditions.items, emptyCondition()] } }));

  const setAction = (idx, next) => setForm((f) => ({ ...f, actions: f.actions.map((a, i) => (i === idx ? next : a)) }));
  const removeAction = (idx) => setForm((f) => ({ ...f, actions: f.actions.filter((_, i) => i !== idx) }));
  const addAction = () => setForm((f) => ({ ...f, actions: [...f.actions, emptyAction()] }));

  const validConditions = form.conditions.items.filter((c) => c.field && c.operator && (NO_VALUE_OPERATORS.includes(c.operator) || String(c.value ?? '').length > 0));
  const validActions = form.actions.filter((a) => a.type && actionIsComplete(a));
  const valid = form.name.trim().length >= 2 && validConditions.length > 0 && validActions.length > 0;

  const handleTest = async () => {
    setTestState({ loading: true, error: null, result: null });
    try {
      const payload = formToPayload(form);
      const res = await apiService.automation.testRule({ rule: payload, limit: 50 });
      setTestState({ loading: false, error: null, result: res.data });
    } catch (err) {
      setTestState({ loading: false, error: serverMessage(err, 'Test failed'), result: null });
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{rule ? 'Edit automation rule' : 'New automation rule'}</DialogTitle>
      <DialogContent dividers>
        {serverErrors && serverErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverErrors.length === 1 ? serverErrors[0] : (
              <List dense disablePadding>
                {serverErrors.map((e, i) => <ListItem key={i} disableGutters sx={{ py: 0 }}><ListItemText primary={e} /></ListItem>)}
              </List>
            )}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required disabled={saving} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth type="number" label="Priority" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} inputProps={{ min: 1, max: 1000 }} disabled={saving} helperText="Lower runs first" />
          </Grid>
          <Grid item xs={6} md={3} display="flex" alignItems="center" gap={1}>
            <FormControlLabel control={<Switch checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} disabled={saving} />} label="Enabled" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Description (optional)" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} disabled={saving} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={form.stopProcessing} onChange={(e) => setForm((f) => ({ ...f, stopProcessing: e.target.checked }))} disabled={saving} />}
              label="Stop processing further rules when this matches"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Conditions</Typography>
          <FormControl size="small">
            <Select
              value={form.conditions.match}
              onChange={(e) => setForm((f) => ({ ...f, conditions: { ...f.conditions, match: e.target.value } }))}
              disabled={saving}
            >
              <MenuItem value="all">Match ALL conditions</MenuItem>
              <MenuItem value="any">Match ANY condition</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {form.conditions.items.map((cond, idx) => (
          <ConditionRow
            key={cond.id}
            cond={cond}
            fields={options.fields}
            operators={options.operators}
            onChange={(next) => setCondition(idx, next)}
            onRemove={() => removeCondition(idx)}
            disabled={saving}
          />
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addCondition} disabled={saving}>Add condition</Button>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Actions</Typography>
        {form.actions.map((action, idx) => (
          <ActionRow
            key={action.id}
            action={action}
            options={options}
            onChange={(next) => setAction(idx, next)}
            onRemove={() => removeAction(idx)}
            disabled={saving}
          />
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addAction} disabled={saving}>Add action</Button>

        <Divider sx={{ my: 2 }} />

        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Button size="small" variant="outlined" startIcon={<ScienceIcon />} onClick={handleTest} disabled={saving || testState.loading || validConditions.length === 0 || validActions.length === 0}>
            {testState.loading ? 'Testing...' : 'Test rule'}
          </Button>
          {testState.error && <Alert severity="error" sx={{ flex: 1 }}>{testState.error}</Alert>}
          {testState.result && (
            <Alert severity={testState.result.matched > 0 ? 'success' : 'info'} sx={{ flex: 1 }}>
              Matched {testState.result.matched} of the {testState.result.tested} most recent detections.
              {testState.result.matched > 0 && (
                <Box component="span" display="block" sx={{ mt: 0.5 }}>
                  {testState.result.detections.slice(0, 10).map((d) => d.title).join(' · ')}
                </Box>
              )}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={() => onSave(formToPayload(form))} disabled={saving || !valid}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/* Test result dialog (for the row-level Test button)                  */
/* ------------------------------------------------------------------ */
const TestResultDialog = ({ state, onClose }) => (
  <Dialog open={state.open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Test: {state.ruleName}</DialogTitle>
    <DialogContent dividers>
      {state.loading && <Box display="flex" justifyContent="center" p={3}><CircularProgress size={28} /></Box>}
      {state.error && <Alert severity="error">{state.error}</Alert>}
      {state.result && (
        <>
          <Alert severity={state.result.matched > 0 ? 'success' : 'info'} sx={{ mb: 2 }}>
            Matched {state.result.matched} of the {state.result.tested} most recent detections. No changes were made.
          </Alert>
          {state.result.matched > 0 && (
            <List dense>
              {state.result.detections.slice(0, 10).map((d) => (
                <ListItem key={d._id} divider>
                  <ListItemText
                    primary={d.title}
                    secondary={`${d.severity || ''} · ${d.category || ''} · ${d.source || ''}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

/* ------------------------------------------------------------------ */
/* Automation rules settings                                            */
/* ------------------------------------------------------------------ */
const AutomationRules = () => {
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission('detections', 'update');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [rules, setRules] = useState([]);
  const [options, setOptions] = useState({ fields: [], operators: [], actions: [], playbooks: [], users: [], roles: [] });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState(null);

  const [running, setRunning] = useState(false);
  const [runSummary, setRunSummary] = useState(null);
  const [testResult, setTestResult] = useState({ open: false, loading: false, error: null, result: null, ruleName: '' });
  const [busyId, setBusyId] = useState(null);

  const fieldsById = useMemo(() => Object.fromEntries((options.fields || []).map((f) => [f.id, f])), [options.fields]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [optRes, rulesRes] = await Promise.all([
        apiService.automation.getOptions(),
        apiService.automation.getRules(),
      ]);
      setOptions({
        fields: optRes.data?.fields || [],
        operators: optRes.data?.operators || [],
        actions: optRes.data?.actions || [],
        playbooks: optRes.data?.playbooks || [],
        users: optRes.data?.users || [],
        roles: optRes.data?.roles || [],
      });
      setRules(rulesRes.data?.rules || []);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load automation rules'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!success) return undefined;
    const handle = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(handle);
  }, [success]);

  const handleToggleEnabled = async (rule, enabled) => {
    setRules((prev) => prev.map((r) => (r._id === rule._id ? { ...r, enabled } : r)));
    try {
      setBusyId(rule._id);
      await apiService.automation.updateRule(rule._id, { enabled });
    } catch (err) {
      setRules((prev) => prev.map((r) => (r._id === rule._id ? { ...r, enabled: !enabled } : r)));
      setError(serverMessage(err, 'Failed to update rule'));
    } finally {
      setBusyId(null);
    }
  };

  const handleSave = async (payload) => {
    try {
      setSaving(true);
      setServerErrors(null);
      setError(null);
      if (editingRule) {
        const res = await apiService.automation.updateRule(editingRule._id, payload);
        setRules((prev) => prev.map((r) => (r._id === editingRule._id ? res.data.rule : r)));
        setSuccess('Rule updated');
      } else {
        const res = await apiService.automation.createRule(payload);
        setRules((prev) => [...prev, res.data.rule].sort((a, b) => (a.priority || 100) - (b.priority || 100)));
        setSuccess('Rule created');
      }
      setDialogOpen(false);
      setEditingRule(null);
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 400 && data) {
        setServerErrors(data.errors && data.errors.length ? data.errors : [data.message || 'Validation failed']);
      } else {
        setError(serverMessage(err, 'Failed to save rule'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? This cannot be undone.`)) return;
    try {
      setBusyId(rule._id);
      await apiService.automation.deleteRule(rule._id);
      setRules((prev) => prev.filter((r) => r._id !== rule._id));
      setSuccess('Rule deleted');
    } catch (err) {
      setError(serverMessage(err, 'Failed to delete rule'));
    } finally {
      setBusyId(null);
    }
  };

  const handleTestRule = async (rule) => {
    setTestResult({ open: true, loading: true, error: null, result: null, ruleName: rule.name });
    try {
      const res = await apiService.automation.testRule({ ruleId: rule._id, limit: 50 });
      setTestResult({ open: true, loading: false, error: null, result: res.data, ruleName: rule.name });
    } catch (err) {
      setTestResult({ open: true, loading: false, error: serverMessage(err, 'Test failed'), result: null, ruleName: rule.name });
    }
  };

  const handleRunNow = async () => {
    try {
      setRunning(true);
      setRunSummary(null);
      setError(null);
      const res = await apiService.automation.run({ limit: 100 });
      setRunSummary(`Processed ${res.data.processed} detections, ${res.data.affected} affected.`);
      load();
    } catch (err) {
      setError(serverMessage(err, 'Failed to run rules'));
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {runSummary && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setRunSummary(null)}>{runSummary}</Alert>}
      {!canUpdate && (
        <Alert severity="info" sx={{ mb: 2 }}>You can view automation rules, but only users who can update detections may create, edit, or run them.</Alert>
      )}

      <Card>
        <CardHeader
          avatar={<RuleIcon color="primary" />}
          title="Automation rules"
          subheader="Rules run automatically whenever a new detection is created, in priority order. You can also run them on demand below."
          action={canUpdate && (
            <Box display="flex" gap={1}>
              <Button variant="outlined" size="small" startIcon={<RunIcon />} onClick={handleRunNow} disabled={running}>
                {running ? 'Running...' : 'Run rules now'}
              </Button>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => { setEditingRule(null); setServerErrors(null); setDialogOpen(true); }}>
                New rule
              </Button>
            </Box>
          )}
        />
        <CardContent>
          {rules.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" gutterBottom>No automation rules yet.</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', mb: 2 }}>
                Rules automatically triage detections as they arrive: match on fields like severity, category, or
                source, then apply actions such as changing severity, assigning an analyst, adding tags, or creating
                a case. For example: <em>if severity is Critical AND category is Malware, then assign to a senior
                analyst and create a case.</em>
              </Typography>
              {canUpdate && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingRule(null); setServerErrors(null); setDialogOpen(true); }}>
                  New rule
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={70}>Priority</TableCell>
                    <TableCell>Rule</TableCell>
                    <TableCell width={90}>Enabled</TableCell>
                    <TableCell>Conditions</TableCell>
                    <TableCell>Actions</TableCell>
                    <TableCell>Stats</TableCell>
                    <TableCell width={130} align="right">Controls</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule._id} hover>
                      <TableCell>{rule.priority ?? 100}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{rule.name}</Typography>
                        {rule.description && <Typography variant="caption" color="text.secondary" display="block">{rule.description}</Typography>}
                        {rule.stopProcessing && <Chip label="Stops further rules" size="small" variant="outlined" sx={{ mt: 0.5 }} />}
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={rule.enabled !== false}
                          onChange={(e) => handleToggleEnabled(rule, e.target.checked)}
                          disabled={!canUpdate || busyId === rule._id}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="caption" color="text.secondary">
                          {(rule.conditions?.items || []).map((c) => humanizeCondition(c, fieldsById)).join(rule.conditions?.match === 'any' ? ' OR ' : ' AND ')}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {(rule.actions || []).map((a, i) => (
                            <Chip key={i} label={humanizeAction(a, options)} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {rule.stats?.matches || 0} match{(rule.stats?.matches || 0) === 1 ? '' : 'es'}
                        </Typography>
                        {rule.stats?.lastMatchedAt && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Last: {formatRelativeTime(rule.stats.lastMatchedAt)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Test (dry run, no changes)">
                          <span>
                            <IconButton size="small" onClick={() => handleTestRule(rule)}>
                              <TestIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton size="small" onClick={() => { setEditingRule(rule); setServerErrors(null); setDialogOpen(true); }} disabled={!canUpdate}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton size="small" color="error" onClick={() => handleDelete(rule)} disabled={!canUpdate || busyId === rule._id}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <RuleDialog
        open={dialogOpen}
        rule={editingRule}
        options={options}
        onClose={() => { if (!saving) { setDialogOpen(false); setEditingRule(null); setServerErrors(null); } }}
        onSave={handleSave}
        saving={saving}
        serverErrors={serverErrors}
      />

      <TestResultDialog state={testResult} onClose={() => setTestResult((s) => ({ ...s, open: false }))} />
    </Box>
  );
};

export default AutomationRules;
